import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { LogOut, Loader2, Clock, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import Modal from '../common/Modal';
import { logoutUser } from '../../redux/slices/authSlice';
import { clearShiftState } from '../../redux/slices/shiftSlice';

const formatRs = (n) => `Rs. ${(Number(n) || 0).toLocaleString()}`;

export default function LogoutModal({ isOpen, onClose }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const { currentUser, summary } = useSelector((state) => state.shift);

  const [loggingOut, setLoggingOut] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setLoggingOut(false);
    }
  }, [isOpen]);

  const handleClose = () => {
    if (loggingOut) return;
    onClose();
  };

  const finalizeLogout = () => {
    dispatch(clearShiftState());
    navigate('/login');
  };

  const handleLogoutOnly = async () => {
    setLoggingOut(true);
    try {
      await dispatch(logoutUser());
      toast.success('Logged out. Your shift has ended. The business day stays open.');
      finalizeLogout();
    } catch {
      finalizeLogout();
    }
  };

  const myShift = summary?.users?.find((u) => u.userId === user?.userId) || currentUser;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Logout"
      description="End your shift and log out. Today's business day remains open."
      size="sm"
    >
      <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center">
            <LogOut size={18} className="text-blue-600 dark:text-blue-400" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-gray-900 dark:text-white">End My Shift Only</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              This ends <span className="font-medium text-gray-700 dark:text-gray-300">{user?.name}</span>'s
              shift for today. Another user can log in and keep selling. The business day is not closed.
            </p>
          </div>
        </div>
      </div>

      {myShift && (
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-3 flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/50 rounded-lg px-3 py-2"
        >
          <Clock size={14} />
          My shift: {formatRs(myShift.sales)} sales • {myShift.orders || 0} orders • {formatRs(myShift.profit)} profit
        </motion.div>
      )}

      <div className="mt-3 flex items-start gap-2 text-xs text-gray-400 dark:text-gray-500">
        <AlertCircle size={13} className="mt-0.5 flex-shrink-0" />
        To close the entire business day, use the SALE END button instead.
      </div>

      <div className="mt-5 flex gap-3">
        <button
          onClick={handleClose}
          disabled={loggingOut}
          className="flex-1 px-4 py-2.5 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          onClick={handleLogoutOnly}
          disabled={loggingOut}
          className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loggingOut && <Loader2 size={16} className="animate-spin" />}
          {loggingOut ? 'Ending shift...' : 'Logout'}
        </button>
      </div>
    </Modal>
  );
}